import { useId, type ReactNode } from 'react';

interface GuideExampleProps {
  /** Short caption shown beneath the demo (rendered in <figcaption>). */
  title: string;
  /** Optional one-line description shown below the caption. */
  description?: string;
  children: ReactNode;
}

/**
 * G-5 — wrapper for live component demos embedded inside the moderator
 * guide. Renders a `<figure>` with a labelled bordered card around the
 * children and a `<figcaption>` underneath. The figure is `aria-labelledby`
 * the caption so screen readers announce the demo's name when navigating
 * by figure/region.
 *
 * Design tokens only (no hard-coded colours). Long demos can horizontally
 * scroll on narrow viewports so the body copy never gets pushed wider than
 * the column.
 */
export default function GuideExample({
  title,
  description,
  children,
}: GuideExampleProps) {
  const captionId = useId();
  return (
    <figure
      className="guide-example"
      aria-labelledby={captionId}
      data-testid="guide-example"
    >
      <div className="guide-example__frame">{children}</div>
      <figcaption className="guide-example__caption" id={captionId}>
        <span className="guide-example__title">{title}</span>
        {description ? (
          <span className="guide-example__description">{description}</span>
        ) : null}
      </figcaption>
    </figure>
  );
}
